import { useState } from "react";
import Card from "components/card";
import { formatKes } from "lib/format";
import { useAuth } from "contexts/AuthContext";
import { MoneyFlowLog, PartnerEarningsTiles, PeriodPicker, bpsLabel, useOwnPartnerRevenue } from "components/revenue/RevenueStatement";

function Line({ label, value, strong, negative }) {
  return (
    <div className={`flex items-center justify-between border-t border-gray-100 py-2 text-sm first:border-0 dark:border-white/5 ${strong ? "font-semibold text-navy-700 dark:text-white" : "text-gray-600 dark:text-gray-300"}`}>
      <span>{label}</span>
      <span className="num whitespace-nowrap">
        {negative ? "− " : ""}
        {value}
      </span>
    </div>
  );
}

export default function PartnerRevenuePage() {
  const { portal } = useAuth();
  const [period, setPeriod] = useState("30d");
  const { data: revenue, error } = useOwnPartnerRevenue(period);
  const tenantName = portal?.tenant?.name || "your tenant";
  const p = revenue?.partner;
  const own = p ? p.ownSplit ?? p.split : null;
  const c = revenue?.currency || "KES";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      {error && <div className="rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
      <PartnerEarningsTiles revenue={revenue} portal={portal} />

      <Card extra="p-4 sm:p-5 max-w-2xl">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">How your share is worked out</p>
        <p className="mb-3 mt-1 text-sm text-gray-600 dark:text-gray-300">
          Revenue your own markets earn, plus, if {tenantName} has turned it on, a share of the trading fees on its main markets from positions opened on your page or subdomain. A change to your share applies to revenue earned from then on.
        </p>
        {p ? (
          <div>
            <Line label="Trading commission" value={formatKes(p.tradingCommission, c)} />
            <Line label="Settlement surplus" value={formatKes(p.settlementSurplus, c)} />
            <Line label="Net revenue from your markets" value={formatKes(own.net, c)} strong />
            <Line label={`Platform share (${bpsLabel(10000 - (revenue.tenantShareBps ?? 8000))})`} value={formatKes(own.platform, c)} negative />
            <Line label={`${tenantName}'s share (${bpsLabel(revenue.tenantShareBps)})`} value={formatKes(own.tenantShare, c)} strong />
            <Line label={`${tenantName} keeps`} value={formatKes(own.tenantRetained, c)} negative />
            <Line label={`Your share on your markets (${bpsLabel(p.shareBps)} of ${tenantName}'s share)`} value={formatKes(own.partner, c)} strong />
            {p.main && (p.main.enabled || Number(p.main.split?.partner) > 0) && (
              <>
                <Line label="Trading fees on main markets via your page" value={formatKes(p.main.gross, c)} />
                <Line label={`Your share on main markets (${bpsLabel(p.main.shareBps)} of ${tenantName}'s share)`} value={formatKes(p.main.split?.partner, c)} strong />
              </>
            )}
            {p.main && <Line label="Your share in total" value={formatKes(p.split.partner, c)} strong />}
          </div>
        ) : (
          <div className="h-48 animate-pulse rounded-md bg-accent" />
        )}
      </Card>

      <MoneyFlowLog period={period} variant="partner" title="Your revenue, market by market" />
    </div>
  );
}
